import {
  commaSeparateIds,
  resolveClientIdsFromNames,
  resolveProjectIdsFromNames,
  resolveTeamIdsFromNames,
} from "./timeEntryQueryParams";
import { getResolvedUserIdsForApi } from "./userFilterOptions";
import { formatDate } from "./dateUtils";

/** Display month end using app date format (e.g. Apr 30, 2026). */
export function formatQbReportMonthEndDate(raw) {
  const value =
    raw?.monthEndDate ??
    raw?.month_end_date ??
    raw?.billingMonthEnd ??
    raw?.billingMonthEndDate ??
    raw?.monthEnd ??
    null;
  if (value == null || String(value).trim() === "") return "—";
  const formatted = formatDate(value);
  return formatted === "N/A" ? "—" : formatted;
}

/**
 * Query params for GET /qb/reports (comma-separated UUIDs when multiple selections).
 *
 * @param {object} options
 * @param {object} [options.filters] — `{ client, project, team, user, dateRange }` (client/project/team: names; user: ids from dropdown values)
 * @param {object|null} [options.clientsData]
 * @param {object|null} [options.projectsData]
 * @param {object|null} [options.teamsData]
 */
export function buildQbReportsQueryParams({ filters = {}, clientsData, projectsData, teamsData }) {
  const params = {};

  const dr = filters.dateRange;
  if (dr?.startDate) params.startDate = String(dr.startDate).slice(0, 10);
  if (dr?.endDate) params.endDate = String(dr.endDate).slice(0, 10);

  const cli = filters.client;
  if (Array.isArray(cli) && cli.length > 0 && clientsData) {
    const joined = commaSeparateIds(resolveClientIdsFromNames(cli, clientsData));
    if (joined) params.clientId = joined;
  }

  const proj = filters.project;
  if (Array.isArray(proj) && proj.length > 0 && projectsData) {
    const joined = commaSeparateIds(resolveProjectIdsFromNames(proj, projectsData));
    if (joined) params.projectId = joined;
  }

  const tm = filters.team;
  if (Array.isArray(tm) && tm.length > 0 && teamsData) {
    const joined = commaSeparateIds(resolveTeamIdsFromNames(tm, teamsData));
    if (joined) params.teamId = joined;
  }

  const usr = filters.user;
  if (Array.isArray(usr) && usr.length > 0) {
    const joined = commaSeparateIds(getResolvedUserIdsForApi(usr));
    if (joined) params.userId = joined;
  }

  return params;
}

/** Display string for tag arrays or strings from GET /qb/reports rows. */
export function formatQbReportTagsForDisplay(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value.trim();
  if (!Array.isArray(value)) return String(value).trim();

  const names = value
    .map((item) => {
      if (item == null) return "";
      if (typeof item === "string") return item.trim();
      return String(item.name ?? item.label ?? item.displayName ?? item.tagName ?? "").trim();
    })
    .filter(Boolean);

  return names.join(", ");
}

/**
 * Normalize GET /qb/reports JSON → table rows + summary totals.
 * Shape: `{ success, data: { summary: { totalHours }, data: [...] } }`
 */
export function normalizeQbReportsPreviewResponse(apiBody) {
  const wrapped =
    apiBody?.data != null && typeof apiBody.data === "object" ? apiBody.data : apiBody;
  const summarySrc = wrapped?.summary;
  const list = Array.isArray(wrapped?.data)
    ? wrapped.data
    : Array.isArray(wrapped?.list)
      ? wrapped.list
      : [];

  const summary =
    summarySrc && typeof summarySrc === "object"
      ? {
          totalHours: Number(summarySrc.totalHours) || 0,
        }
      : null;

  const rows = list.map((r, i) => ({
    id:
      r.clientId != null && r.projectId != null
        ? `${r.clientId}-${r.projectId}-${i}`
        : `qb-row-${i}`,
    client: r.clientName ?? r.client ?? "—",
    qbClientName: r.qbClientName ?? r.clientName ?? r.client ?? "—",
    project: r.projectName ?? r.project ?? "—",
    epicName: r.epicName ?? r.epic_name ?? r.epicTitle ?? r.epic_title ?? "",
    monthEndDate: formatQbReportMonthEndDate(r),
    hours: Number(r.billableHours ?? r.hours) || 0,
    clientTags: formatQbReportTagsForDisplay(r.clientTags ?? r.client_tags),
    vendorTags: formatQbReportTagsForDisplay(r.vendorTags ?? r.vendor_tags),
    clientNotes: r.clientNotes ?? "",
    vendorNotes: r.vendorNotes ?? "",
  }));

  return { rows, summary };
}
